import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { Upload, FileSpreadsheet, CheckCircle, AlertCircle } from "lucide-react";

const IGNORE_VALUE = "__ignore__";

export default function UploadPage() {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<any>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [entityType, setEntityType] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const { toast } = useToast();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setResult(null);
    setLoading(true);

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const response = await fetch("/api/upload/preview", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) throw new Error("Erro ao processar arquivo");

      const data = await response.json();
      setPreview(data);

      const initialMapping: Record<string, string> = {};
      data.columns.forEach((col: string) => {
        initialMapping[col] = IGNORE_VALUE;
      });
      setMapping(initialMapping);

      toast({
        title: "Preview carregado!",
        description: `${data.totalRows} linhas encontradas`,
      });
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file || !entityType) {
      toast({
        title: "Campos obrigatorios",
        description: "Selecione um arquivo e o tipo de entidade",
        variant: "destructive",
      });
      return;
    }

    const cleanMapping: Record<string, string> = {};
    for (const [col, field] of Object.entries(mapping)) {
      if (field && field !== IGNORE_VALUE) {
        cleanMapping[col] = field;
      }
    }

    setLoading(true);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("mapping", JSON.stringify(cleanMapping));
    formData.append("entityType", entityType);

    try {
      const response = await fetch("/api/upload/import", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) throw new Error("Erro ao importar dados");

      const data = await response.json();
      setResult(data);

      toast({
        title: "Importacao concluida!",
        description: `${data.imported} de ${data.total} registros importados`,
      });
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fieldOptions = [
    { value: "name", label: "Nome" },
    { value: "cpfCnpj", label: "CPF/CNPJ" },
    { value: "code", label: "Codigo" },
    { value: "email", label: "Email" },
    { value: "phone", label: "Telefone" },
    { value: "cellphone", label: "Celular" },
    { value: "address", label: "Endereco" },
    { value: "neighborhood", label: "Bairro" },
    { value: "city", label: "Cidade" },
    { value: "state", label: "Estado" },
    { value: "cep", label: "CEP" },
    { value: "region", label: "Regiao" },
    { value: "category", label: "Categoria" },
    { value: "segment", label: "Segmento" },
    { value: "segment2", label: "Segmento 2" },
    { value: "birthDate", label: "Data de Nascimento" },
    { value: "deliveryAddress", label: "Endereco de Entrega" },
    { value: "deliveryNeighborhood", label: "Bairro de Entrega" },
    { value: "deliveryCity", label: "Cidade de Entrega" },
    { value: "deliveryState", label: "Estado de Entrega" },
    { value: "deliveryCep", label: "CEP de Entrega" },
    { value: "subCategory", label: "Subcategoria" },
    { value: "productType", label: "Tipo de Produto" },
  ];

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
          <Upload className="w-5 h-5 text-blue-400" />
        </div>
        <div>
          <h2 className="text-2xl font-bold" data-testid="text-page-title">Importar Planilha Excel</h2>
          <p className="text-muted-foreground">Faca upload de arquivos .xlsx e importe dados em lote</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>1. Selecione o arquivo</CardTitle>
          <CardDescription>Formatos aceitos: .xlsx (Excel)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary transition-colors">
            <FileSpreadsheet className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <Label htmlFor="file-upload" className="cursor-pointer">
              <span className="text-primary hover:underline font-medium">
                Clique para selecionar
              </span>
              <span className="text-muted-foreground"> ou arraste o arquivo aqui</span>
            </Label>
            <Input
              id="file-upload"
              type="file"
              accept=".xlsx"
              onChange={handleFileChange}
              className="hidden"
              data-testid="input-file-upload"
            />
          </div>

          {file && (
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>
                Arquivo selecionado: <strong>{file.name}</strong>
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {preview && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>2. Configure o tipo de entidade</CardTitle>
              <CardDescription>Defina que tipo de dados esta importando</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label htmlFor="entity-type">Tipo de Entidade</Label>
                <Select value={entityType} onValueChange={setEntityType}>
                  <SelectTrigger id="entity-type" data-testid="select-import-entity-type">
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="propriedade">Propriedade Rural</SelectItem>
                    <SelectItem value="produtor">Produtor</SelectItem>
                    <SelectItem value="produto">Produto</SelectItem>
                    <SelectItem value="ativo">Ativo</SelectItem>
                    <SelectItem value="fornecedor">Fornecedor</SelectItem>
                    <SelectItem value="concorrente">Concorrente</SelectItem>
                    <SelectItem value="outro">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>3. Mapeie as colunas</CardTitle>
              <CardDescription>
                {preview.totalRows} linhas - {preview.columns.length} colunas encontradas
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {preview.columns.map((col: string) => (
                  <div key={col} className="flex items-center gap-4">
                    <div className="flex-1 font-medium text-sm">
                      {col}
                    </div>
                    <div className="flex-1">
                      <Select
                        value={mapping[col] || IGNORE_VALUE}
                        onValueChange={(value) =>
                          setMapping({ ...mapping, [col]: value })
                        }
                      >
                        <SelectTrigger data-testid={`select-mapping-${col}`}>
                          <SelectValue placeholder="Ignorar" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={IGNORE_VALUE}>Ignorar</SelectItem>
                          {fieldOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                          <SelectItem value={`extra_${col}`}>Dados Adicionais: {col}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>4. Preview dos dados</CardTitle>
              <CardDescription>Primeiras 5 linhas da planilha</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      {preview.columns.slice(0, 6).map((col: string) => (
                        <th key={col} className="text-left p-2 font-medium">
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.preview.map((row: any, idx: number) => (
                      <tr key={idx} className="border-b">
                        {preview.columns.slice(0, 6).map((col: string) => (
                          <td key={col} className="p-2">
                            {String(row[col] || "").slice(0, 50)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-4">
            <Button
              onClick={handleImport}
              disabled={loading || !entityType}
              className="flex-1"
              size="lg"
              data-testid="button-import"
            >
              {loading ? "Importando..." : "Importar Dados"}
            </Button>
          </div>
        </>
      )}

      {result && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              Importacao Concluida
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-green-500/10 p-4 rounded-lg" data-testid="text-imported-count">
                <div className="text-2xl font-bold text-green-400">
                  {result.imported}
                </div>
                <div className="text-sm text-green-400/80">Importados</div>
              </div>
              <div className="bg-muted p-4 rounded-lg">
                <div className="text-2xl font-bold text-foreground">
                  {result.total}
                </div>
                <div className="text-sm text-muted-foreground">Total</div>
              </div>
              <div className="bg-red-500/10 p-4 rounded-lg">
                <div className="text-2xl font-bold text-red-400">
                  {result.errors?.length || 0}
                </div>
                <div className="text-sm text-red-400/80">Erros</div>
              </div>
            </div>

            {result.errors && result.errors.length > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <div className="font-semibold mb-2">Erros encontrados:</div>
                  <div className="space-y-1 text-xs">
                    {result.errors.slice(0, 5).map((error: any, idx: number) => (
                      <div key={idx}>
                        Linha {error.row}: {error.error}
                      </div>
                    ))}
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
